import { SDKConfig } from '../sdk'
import events, { EventsTrait, EventBus } from '../events/bus'
import { Logger } from '../utils/logger'

import { useToken } from '../services/api'

import { SubscriptionApi, SubscriptionResponse, SubscriptionServiceLevel } from '../services/api/account'
import { StashApi, StashInfoResponse } from '../services/api/stash'
import { SDK_AUTH_STATUS_CHANGE } from '../events/types/sdk.events'
import { SDKCustomEvent } from '../events'
import { SUBSCRIPTION_STATUS } from '../constants/enums'
import { SIRIUS_XM_PLAN } from '../constants'


type Tokens = {
  token: string
  legacy_token: string
  legacy_uguid: string
}

const enum SERVICE_LEVEL {
  HQ = 'highquality',
  STANDARD = 'regular'
}


class Account extends EventsTrait {
  #id: string
  #email: string
  #tokens: Tokens
  #config: SDKConfig
  #events: EventBus
  #auth: boolean

  public static logger: Logger

  public stash: Stash
  public subscription: Subscription

  get events() {
    return this.#events
  }

  get name() {
    return 'Account'
  }

  public get id() {
    return this.#id
  }

  public get email() {
    return this.#email
  }

  get tokens() {
    return this.#tokens
  }

  public get config() {
    return this.#config
  }

  /**
   * @description Check if user is an registered subscriber now or was in the past.
   * (doesn't mean he has access to content or subscription is active)
   */
  public get isSubscriber() {
    return !!this.subscription.id && this.isAuthorized
  }

  /**
   * @description Check if user is an registered subscriber now and has access to content.
   * (means he has access to content and subscription is active)
   */
  public get isSubscribed() {
    return this.isAuthorized && !!this.subscription.id && this.subscription.isContentAccessible
  }

  public get isAuthorized() {
    return !!(this.tokens.token && this.email && this.#auth)
  }

  public get hasAccessToContent() {
    return this.isAuthorized && (this.subscription.isContentAccessible || this.stash.hasContent)
  }

  public get isLosslessAvailable() {
    return this.isAuthorized && this.subscription.isLosslessAvailable
  }

  constructor(id: string, email: string, tokens: Tokens, config: SDKConfig) {
    super()

    this.#id = id
    this.#email = email
    this.#tokens = tokens
    this.#config = config
    this.#events = events
    this.#auth = !!tokens
    this.subscription = new Subscription()
    this.stash = new Stash()


    this.on(SDK_AUTH_STATUS_CHANGE.type, (event: SDKCustomEvent<{ isAuthorized: boolean }>) => {
      this.#auth = event.detail.isAuthorized
    })

    Subscription.logger = Account.logger
    Stash.logger = Account.logger
  }

  public async fetch() {
    const log = Account.logger.YELLOW.OUTLINE

    if (this.tokens.token) {
      useToken(this.tokens.token)

      log(this.name, '[fetch:start]')

      const [ subscription, stash ] = await Promise.all([
        // Load and check stash
        this.config.app.components?.subscription !== false && Subscription.fetch(),
        // Load and check subscription
        this.config.app.components?.stash !== false && Stash.fetch()
      ])

      if (subscription) this.subscription = subscription
      if (stash) this.stash = stash

      this.#auth = true

      log(this.name, '[fetch:end]', { subscription, stash })
    }

    return
  }
}
export const statusColor = (status: SUBSCRIPTION_STATUS) => {
  switch (status) {
    case SUBSCRIPTION_STATUS.GRACE_PERIOD:
      return 'yellow'
    case SUBSCRIPTION_STATUS.ON_HOLD:
      return 'orange'
    case SUBSCRIPTION_STATUS.CANCEL_AT:
      return 'red'
    default:
      return 'green'
  }
}

class Subscription {
  public static logger: Logger

  #data?: SubscriptionResponse
  #levels: SubscriptionServiceLevel[]

  static get className() {
    return 'Subscription'
  }

  get legacyId() {
    return this.#data?.legacySubscriptionId
  }

  get id() {
    return this.#data?.id
  }

  get startedAt() {
    return this.#data?.startedAt
  }

  get endsAt() {
    return this.#data?.endsAt
  }

  get plan() {
    return this.#data?.plan?.planId || this.#data?.promo?.plan.id || this.#data?.plan?.id
  }

  get description() {
    if (!this.id) return ''
    return this.level === SERVICE_LEVEL.HQ ? 'HiFi Streaming' : 'Premium Streaming'
  }

  get levelsNames() {
    return this.#levels.map(level => level.name.toLowerCase() as SERVICE_LEVEL)
  }

  get levels() {
    return [...this.#levels]
  }

  get level() {
    if (!this.id) return null
    const level = this.#data?.plan?.serviceLevel || this.#data?.promo?.plan.serviceLevel
    return typeof level === 'string' ? level.toLowerCase() as SERVICE_LEVEL : null
  }

  get period() {
    return (this.#data?.promo?.plan.period || this.#data?.plan?.period) || 30
  }

  get price() {
    return Number((this.#data?.promo?.plan.price || this.#data?.plan?.price) || 0).toFixed(2) || '0.00'
  }

  get type() {
    return this.period === 30 ? 'month' : 'year'
  }

  get status() {
    switch (this.#data?.status) {
      case SUBSCRIPTION_STATUS.ACTIVE_PAID:
        return 'Subscribed'
      case SUBSCRIPTION_STATUS.ON_HOLD:
        return 'Suspended'
      case SUBSCRIPTION_STATUS.CANCEL_AT:
        return 'Canceled'
      case SUBSCRIPTION_STATUS.GRACE_PERIOD:
        return 'Expired'
      default:
        return (this.#data?.status) as SUBSCRIPTION_STATUS
    }
  }

  get isTrial() {
    return String(this.#data?.status).toLowerCase() === 'trial'
  }

  get isEnded() {
    return String(this.#data?.status).toLowerCase() === 'ended'
  }

  get isCanceled() {
    return String(this.status).toLowerCase() === 'canceled'
  }

  get isTrialAvailable() {
    return !!this.data?.isTrialAvailable
  }

  get isLosslessAvailable(): boolean {
    return (
      !!this.id &&
      this.isContentAccessible &&
      String(this.#data?.plan?.serviceLevel || this.#data?.promo?.plan.serviceLevel || '').toLowerCase() === SERVICE_LEVEL.HQ
    )
  }

  get isVideoContentAvailable() {
    // If there are no levels, we don't have any information about video content access
    if (!this.#levels.length) return false

    const level = this.#levels.find(level => level.name.toLowerCase() === this.level)

    // If we can't find the level property "isVideoAvailable", we assume that video content is available by default
    return level?.isVideoAvailable ?? true
  }

  get isContentAccessible() {
    return !!this.id && !!this.#data?.isContentAccessible
  }

  get isSiriusXM() {
    return String(this.#data?.plan?.planId).toLowerCase() === SIRIUS_XM_PLAN
  }

  get data() {
    return this.#data
  }

  constructor(data?: SubscriptionResponse, levels?: SubscriptionServiceLevel[]) {
    this.#data = data || {} as SubscriptionResponse
    this.#levels = levels ?? []
  }

  static async fetch() {
    const api = new SubscriptionApi('/api')

    const [r1, r2] = await Promise.allSettled([
      api.fetchSubscription(),
      api.fetchLevels()
    ])

    if (r1.status === 'rejected' || !r1.value) {
      throw new Error('Failed to fetch subscription')
    }

    const data = r1.value as SubscriptionResponse
    const levels = r2.status === 'fulfilled' ? r2.value : []

    Subscription.logger.YELLOW.OUTLINE('Account', '[fetch:subscription]')

    return new Subscription(data as never, levels)
  }
}

class Stash {
  #data: StashInfoResponse

  public static logger: Logger

  static get className() {
    return 'Stash'
  }

  get ready() {
    return !!this.#data && !!this.#data.userId
  }

  static async fetch() {

    const api = new StashApi('/api')
    const data = await api.fetchStash()

    Subscription.logger.YELLOW.OUTLINE('Account', '[fetch:stash]')

    return new Stash(data)
  }

  get hasContent() {
    return this.ready && +this.#data.itemsCount > 0
  }

  constructor(data?: StashInfoResponse) {
    this.#data = data || { itemsCount: 0, userId: null }
  }
}

export { Account, Subscription }
